import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { DateTime } from "luxon";
import { IconButton } from "@mui/material";
import { DatePicker, DateView } from "@mui/x-date-pickers-pro";
import ClearIcon from "@mui/icons-material/Clear";

import {
  datePickerVariantSx,
  datePickerDenseSx,
  DatePickerVariant,
} from "./styles";

const ResetButton = ({ onReset }: { onReset: () => void }) => {
  return (
    <IconButton
      onClick={onReset}
      aria-label="reset"
      sx={{
        order: 1,
        padding: 0,
        fontSize: "1em",
        opacity: 0,
        "&:focus-visible": { opacity: 1 },
        color: "text.secondary",
      }}
    >
      <ClearIcon sx={{ fontSize: "1.25em" }} />
    </IconButton>
  );
};

const toDateTime = (
  v: DateTime | string | null | undefined
): DateTime | null => {
  if (!v) return null;
  if (typeof v === "string") return DateTime.fromISO(v);
  return v;
};

/**
 * Single date picker — standalone, no Formik.
 * Use in filter bars, app settings, and any date input outside a Formik form.
 *
 * - `value` / `onChange`: ISO string (YYYY-MM-DD) or Luxon DateTime object
 * - `variant`: "filled" (default, no border) or "outlined" (with border, no background)
 * - `clearable`: enables the native MUI × button (replaces the hover reset button)
 * - `error` / `helperText`: allows external Formik error display
 */
const CustomDatePicker = ({
  label,
  translateLabel = true,
  value,
  maxDate,
  minDate,
  views,
  disabled,
  clearable,
  dense = false,
  error,
  helperText,
  variant = "filled",
  onChange,
}: {
  label: string | React.ReactNode;
  translateLabel?: boolean;
  value?: DateTime | string | null;
  maxDate?: DateTime<boolean>;
  minDate?: DateTime<boolean>;
  views?: DateView[];
  disabled?: boolean;
  clearable?: boolean;
  dense?: boolean;
  error?: boolean;
  helperText?: React.ReactNode;
  variant?: DatePickerVariant;
  onChange: (newValue: string | null) => void;
}) => {
  const intl = useIntl();
  const [date, setDate] = useState<DateTime | null>(toDateTime(value));

  useEffect(() => {
    setDate(toDateTime(value));
  }, [value]);

  const handleReset = () => {
    setDate(null);
    onChange(null);
  };

  const handleChange = (newDate: DateTime | null) => {
    setDate(newDate);
    // Ignore invalid dates (partial keyboard input): toISODate() would return
    // null, and the parent echoing null back through `value` would wipe the
    // sections the user is still typing.
    if (newDate === null || newDate.isValid) {
      onChange(newDate ? newDate.toISODate() : null);
    }
  };

  const resolvedLabel =
    typeof label === "string" && translateLabel
      ? intl.formatMessage({ id: label })
      : label;

  return (
    <DatePicker
      closeOnSelect
      label={resolvedLabel}
      onChange={handleChange}
      maxDate={maxDate}
      minDate={minDate}
      value={date}
      views={views}
      disabled={disabled}
      slotProps={{
        toolbar: { hidden: true },
        ...(clearable && { field: { clearable: true } }),
        textField: {
          size: "small",
          error,
          helperText,
          sx: [
            datePickerVariantSx[variant],
            ...(dense ? [datePickerDenseSx] : []),
            ...(!clearable
              ? [
                  {
                    "&:hover .MuiButtonBase-root.MuiIconButton-root, &:focus-within .MuiButtonBase-root.MuiIconButton-root":
                      {
                        opacity: 1,
                      },
                    "& .MuiInputAdornment-positionEnd": { height: "0.01em" },
                  },
                ]
              : []),
          ],
          ...((dense || !clearable) && {
            slotProps: {
              ...(dense && { inputLabel: { shrink: true } }),
              ...(!clearable && {
                input: {
                  startAdornment: <ResetButton onReset={handleReset} />,
                },
              }),
            },
          }),
        },
        ...(!clearable && {
          inputAdornment: {
            position: "end",
            sx: { order: 2, marginLeft: 0 },
          },
        }),
      }}
    />
  );
};

export default CustomDatePicker;
